import {
  CarcassonneModuleId,
  FigureRule,
  FigureTableRow,
} from '../types';
import { getModuleById } from './modules';

/**
 * Shared pawns/figures catalogue. Filter with `buildFiguresTable` for the
 * active module set — do not leave figure rules only in expansion chapters.
 */
export const carcassonneFigureRules: FigureRule[] = [
  // ——— Base ———
  {
    id: 'fig-normal-meeple',
    name: 'Zwykły poddany (meeple)',
    moduleId: 'base',
    wherePlaced:
      'Na właśnie dołożonym kafelku: podróżnik na drodze, rycerz w mieście, mnich w klasztorze albo chłop na polu (chłop leży).',
    whenPlaced:
      'Etap 2 tury — opcjonalnie jedna figura po dołożeniu kafelka (wyjątki dają rozszerzenia).',
    majorityStrength: '1',
    scoring:
      'Punktuje obszar, na którym stoi, według zasad tego obszaru (droga 1/kafelek; miasto 2+2 herb ukończone / 1+1 nieukończone; klasztor 9 / 1+sąsiedzi; pole 3 za ukończone miasto na końcu). Remis większości = pełne punkty dla każdego remisującego.',
    whenReturns:
      'Po punktowaniu ukończonego obszaru wraca do zapasu. Chłopi zwykle zostają do punktowania końcowego pól.',
    restrictions:
      'Nie wolno wejść na połączony obszar, jeśli jest już na nim jakikolwiek meeple. Jeden meeple koloru służy jako znacznik na torze punktacji i nie jest wystawiany na planszę. To figura gracza — liczy się do większości.',
    isPlayerMeeple: true,
    contributesToMajority: true,
    order: 10,
    keywords: ['meeple', 'poddany', 'podróżnik', 'rycerz', 'mnich', 'chłop', 'chlop'],
  },

  // ——— Opat ———
  {
    id: 'fig-abbot',
    name: 'Opat',
    moduleId: 'abbot',
    wherePlaced:
      'Wyłącznie na klasztorze lub ogrodzie na właśnie dołożonym kafelku (nie na drodze, w mieście ani na polu).',
    whenPlaced:
      'Etap 2 tury zamiast zwykłego meepla; albo w tej samej turze możesz zamiast stawiania figury wycofać już wystawionego opata.',
    majorityStrength: '1',
    scoring:
      'Ukończony klasztor/ogród z opatem: 9 punktów. Wcześniejsze wycofanie z nieukończonego: 1 punkt za kafelek klasztoru/ogrodu + 1 za każdego obecnego sąsiada (także po przekątnych).',
    whenReturns:
      'Po punktowaniu ukończenia albo natychmiast po wcześniejszym wycofaniu (z punktami za obecny stan).',
    restrictions:
      'Nie można jednocześnie postawić figury i wycofać opata. Opat ma siłę 1 i jest meeplem gracza — liczy się do większości na klasztorze/ogrodzie. Ogród punktuje jak klasztor.',
    isPlayerMeeple: true,
    contributesToMajority: true,
    order: 50,
    keywords: ['opat', 'abbot', 'ogród', 'ogrod'],
  },

  // ——— Major 01 ———
  {
    id: 'fig-large-meeple',
    name: 'Duży poddany (duży meeple)',
    moduleId: 'major-01-inns-cathedrals',
    wherePlaced:
      'Jak zwykły meeple: droga, miasto, klasztor lub pole na właśnie dołożonym kafelku (wolny obszar).',
    whenPlaced:
      'Etap 2 tury zamiast zwykłego meepla — masz jednego dużego meepla w zapasie (oraz komponenty 6. gracza według tego dodatku).',
    majorityStrength: '2',
    scoring:
      'Punktuje dokładnie jak zwykły meeple na danym obszarze. Siła 2 wpływa wyłącznie na ustalenie większości — nie podwaja punktów za kafelki ani herby.',
    whenReturns:
      'Po punktowaniu obszaru wraca do zapasu jak zwykły meeple (chłop zazwyczaj do końca gry).',
    restrictions:
      'To meeple gracza. Przy większości liczy się jako siła 2 (nie jako dwa osobne obszary). Nie mylić z podwajaniem punktów. Z minikurierami: przy wiadomościach liczących rycerzy/chłopów duży meeple liczy się jako 1.',
    isPlayerMeeple: true,
    contributesToMajority: true,
    order: 110,
    keywords: ['duży meeple', 'duzy meeple', 'large meeple', 'poddany'],
  },

  // ——— Major 02 ———
  {
    id: 'fig-builder',
    name: 'Budowniczy',
    moduleId: 'major-02-traders-builders',
    wherePlaced:
      'Na drodze lub w mieście razem z właśnie stawianym własnym podróżnikiem/rycerzem — nie stoi sam na pustym obszarze.',
    whenPlaced:
      'Etap 2: przy wystawianiu podróżnika lub rycerza możesz jednocześnie przypisać budowniczego do tego samego obszaru.',
    majorityStrength: '0 (nie liczy się do większości)',
    scoring:
      'Sam nie przyznaje punktów. Gdy przedłużysz obszar z budowniczym nowym kafelkiem: po zakończeniu bieżącej tury odzyskujesz budowniczego i wykonujesz drugą pełną turę.',
    whenReturns:
      'Przed dodatkową turą — zbierasz go do zapasu, potem grasz drugą turę. Po scoringu obszaru wraca też powiązany meeple według zwykłych zasad.',
    restrictions:
      'Siła 0 — nie jest meeplem do większości i nie punktuje samodzielnie. Przestawienie promu, które przedłuża drogę z budowniczym, nie daje podwójnej tury — potrzebujesz faktycznie dołożyć nowy kafelek na tę drogę.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 200,
    keywords: ['budowniczy', 'builder'],
  },
  {
    id: 'fig-pig',
    name: 'Świnia',
    moduleId: 'major-02-traders-builders',
    wherePlaced: 'Na polu (figura specjalna Kupców i Budowniczych C3.1).',
    whenPlaced:
      'Etap 2 tury zamiast zwykłego meepla na polu właśnie dołożonego kafelka (zgodnie z zasadami dodatku).',
    majorityStrength: '0 (nie liczy się do większości pól)',
    scoring:
      'Po ukończeniu miasta z co najmniej 1 herbem: każdy gracz ze świnią na polu stykającym się z tym miastem dostaje 3 punkty × liczbę ukończonych miast przy polu świni. Opcjonalnie możesz wtedy zabrać też chłopa z tego pola.',
    whenReturns:
      'Po tym punktowaniu świnia wraca do zapasu. Nie punktuje na końcu gry (C3.1 — w odróżnieniu od starych wydań).',
    restrictions:
      'Siła 0 — nie tworzy większości na polu. Nie mylić ze starą świnią końcową. To figura gracza specjalna, ale nie meeple do przewagi.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 210,
    keywords: ['świnia', 'swinia', 'pig'],
  },

  // ——— Major 03 ———
  {
    id: 'fig-dragon',
    name: 'Smok',
    moduleId: 'major-03-dragon-fairy',
    wherePlaced:
      'Neutralna figura na planszy; porusza się po kafelkach wzdłuż prostych pasów.',
    whenPlaced:
      'Przy dołożeniu kafelka z symbolem smoka: PRZED orientacją/dołożeniem wykonaj dokładnie 2 ruchy smoka prosto do końca pasa kafelków (usuwa meeple z odwiedzonych pól), potem dokończ dołożenie.',
    majorityStrength: 'Nie dotyczy — nie jest meeplem gracza i nie tworzy większości',
    scoring: 'Sam nie przyznaje punktów; usuwa meeple z kafelków, które odwiedzi.',
    whenReturns:
      'Pozostaje na planszy jako figura neutralna (nie wraca do zapasu gracza).',
    restrictions:
      'Nie mylić z sześcioma ruchami po jednym polu z Księżniczki i Smoka (C1/C2). Meeple z wróżką jest chroniony. Smok nie jest meeplem gracza.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 300,
    keywords: ['smok', 'dragon'],
  },
  {
    id: 'fig-fairy',
    name: 'Wróżka',
    moduleId: 'major-03-dragon-fairy',
    wherePlaced:
      'Przypisywana do wybranego własnego meepla na planszy (nie zajmuje osobnego obszaru jako meeple).',
    whenPlaced:
      'Po ukończeniu drogi lub miasta bez własnych punktów możesz przypisać wróżkę do swojego meepla. Możesz też przenosić ją według zasad dodatku.',
    majorityStrength: 'Nie dotyczy — nie dodaje siły do większości',
    scoring:
      '+3 punkty przy punktowaniu meepla/obszaru, do którego przypisano wróżkę. Chroni tego meepla przed smokiem.',
    whenReturns:
      'Pozostaje przy meeplu do przeniesienia lub końca powiązania według zasad dodatku; nie jest zdejmowana jak meeple po scoringu obszaru automatycznie jako „zapas gracza”.',
    restrictions:
      'Figura neutralna — nie jest meeplem gracza i nie liczy się do większości. Nie mylić z Księżniczką i Smokiem.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 310,
    keywords: ['wróżka', 'wrozka', 'fairy'],
  },

  // ——— Major 04 ———
  {
    id: 'fig-tower-guard',
    name: 'Strażnik (wieża)',
    moduleId: 'major-04-towers-thieves',
    wherePlaced:
      'Na wieży: zamiast stawiania meepla na nowym kafelku możesz ustawić meepla jako strażnika na wieży w zasięgu (zasięg = liczba pięter, max 5).',
    whenPlaced:
      'Etap 2 tury — opcja strażnika zamiast zwykłego umieszczenia na dołożonym kafelku. Fundament + piętro budujesz osobno według zasad Wież.',
    majorityStrength:
      'Nie dotyczy obszarów drogi/miasta/klasztoru/pola — strażnik nie buduje większości na feature mapy',
    scoring:
      'Zmiana strażnika: poprzedni właściciel dostaje +2 punkty. Na końcu gry: 5 punktów za strażnika na wieży (+ ewentualnie skradziony kafelek przy symbolu złodzieja).',
    whenReturns:
      'Przy zmianie strażnika poprzedni wraca; przy symbolu złodzieja: punktuj strażników, potem wracają przed dołożeniem. Na końcu po punktacji zbierasz strażników.',
    restrictions:
      'C3.1: bez porywania meepli z mapy (nie mylić ze starą Wieżą). Strażnik to Twój meeple na wieży, ale nie punktuje drogi/miasta jak zwykły podróżnik/rycerz.',
    isPlayerMeeple: true,
    contributesToMajority: false,
    order: 400,
    keywords: ['strażnik', 'straznik', 'wieża', 'wieza', 'watchman'],
  },

  // ——— Major 05 (C3.1 — nie Opactwo i Burmistrz / wóz) ———
  {
    id: 'fig-mayor',
    name: 'Burmistrz',
    moduleId: 'major-05-messengers-mayors',
    wherePlaced:
      'W mieście na właśnie dołożonym kafelku — wolno także w mieście już zajętym przez meepla lub innego burmistrza.',
    whenPlaced: 'Etap 2 tury zamiast zwykłego meepla.',
    majorityStrength: '0',
    scoring:
      'Przy punktowaniu miasta burmistrz ma siłę 0 (zwykły meeple ma 1) — sam nie wygrywa większości. Favor: gdy kafelek burmistrza dostanie sąsiadów z 4 ortogonalnych stron, możesz wymienić 1 zwykłego meepla przeciwnika w tym mieście na swojego zwykłego meepla z zapasu, po czym burmistrz wraca.',
    whenReturns:
      'Po aktywacji favor (nawet jeśli z niego nie skorzystasz); po punktowaniu miasta; albo gdy ratusz dołożony ortogonalnie obok zwołuje burmistrzów na „naradę” bez favor. Bierze też udział w punktowaniu końcowym, jeśli nadal stoi.',
    restrictions:
      'To meeple gracza, ale siła dokładnie 0 przy przewadze. Nie wolno stawiać rycerza w mieście zajętym tylko przez burmistrza (burmistrz zajmuje miasto). Nie mylić z Opactwem i Burmistrzem C1/C2 ani z wozem — w C3.1 nie ma wozu.',
    isPlayerMeeple: true,
    contributesToMajority: true,
    order: 500,
    keywords: ['burmistrz', 'mayor'],
  },
  {
    id: 'fig-messenger-c31',
    name: 'Kurier (C3.1)',
    moduleId: 'major-05-messengers-mayors',
    wherePlaced:
      'Na drodze na właśnie dołożonym kafelku (droga bez meepla — jak przy zwykłym podróżniku).',
    whenPlaced: 'Etap 2 tury zamiast zwykłego meepla.',
    majorityStrength: '1',
    scoring:
      'Na drodze siła 1 jak zwykły meeple — droga punktuje 1/kafelek (z premiami dodatków). Po scoringu ukończonej drogi możesz: wrócić do zapasu ALBO przenieść kuriera na nieukończoną drogę przy węźle końcowym albo do nieukończonego miasta na końcu drogi (nawet zajęte). W mieście też ma siłę 1; po scoringu miasta wraca.',
    whenReturns:
      'Po scoringu drogi (jeśli nie przenosisz); po scoringu miasta, do którego wjechał; albo gdy zdecydujesz wrócić do zapasu zamiast jazdy dalej.',
    restrictions:
      'Nie mylić z minidodatkiem Wiadomości (kurierzy na torze). To meeple gracza liczący się do większości. Przy wielu kurierach na scorowanej drodze rozstrzygaj zgodnie z ruchem wskazówek od aktywnego.',
    isPlayerMeeple: true,
    contributesToMajority: true,
    order: 510,
    keywords: ['kurier', 'messenger', 'posłaniec', 'poslaniec'],
  },
  {
    id: 'fig-scarecrow',
    name: 'Strach na wróble',
    moduleId: 'major-05-messengers-mayors',
    wherePlaced:
      'Na polu na właśnie dołożonym kafelku — wolno na polu już zajętym przez strach lub innego meepla.',
    whenPlaced: 'Etap 2 tury zamiast zwykłego meepla.',
    majorityStrength: '0 stojący; 1 leżący (jako farmer)',
    scoring:
      'Gdy kafelek stracha dostanie 4 ortogonalnych sąsiadów, możesz: (A) postawić zwykłego meepla jako chłopa obok stracha i zabrać strach, albo (B) położyć strach (leży = farmer siły 1). Jeśli nie wybierzesz A/B — strach wraca. Przy punktowaniu pól leżący strach ma siłę 1; stojący 0.',
    whenReturns:
      'Po aktywacji bez wyboru A/B; po wymianie na chłopa (opcja A); leżący zostaje do scoringu pól jak farmer.',
    restrictions:
      'Ponieważ strach jest meeplem, nie stawiaj chłopa na pole ze strachem. Nie mylić ze stodołą z Zamków i Mostów. Stojący nie tworzy większości (siła 0).',
    isPlayerMeeple: true,
    contributesToMajority: true,
    order: 520,
    keywords: ['strach', 'scarecrow', 'wróble', 'wroble'],
  },

  // ——— Major 09 ———
  {
    id: 'fig-shepherd',
    name: 'Pasterz',
    moduleId: 'major-09-sheep-shepherds',
    wherePlaced:
      'Na polu zamiast meepla; nie na pole z innym pasterzem. Nie stawiaj chłopa na pole z pasterzem.',
    whenPlaced:
      'Etap 2 zamiast zwykłego meepla — po wystawieniu losujesz żeton stada (owca / wilk itd.).',
    majorityStrength: 'Nie dotyczy przewagi chłopów — pasterz nie jest farmerem do 3 pkt/miasto',
    scoring:
      'Przy wypasie (dobrowolnym przy przedłużeniu pola lub obowiązkowym na otoczonym polu): każdy pasterz na polu dostaje pełną liczbę owiec ze żetonów; aktywny +1 za każdy kafelek z gęsiami przy pasterzu (ortog./ukośnie). Wilk: 0 punktów — żetony i pasterze z połączonego pola wracają.',
    whenReturns:
      'Po wypasie; przy wilku natychmiast; owce/gęsi niewypasione nie punktują na końcu.',
    restrictions:
      'Bez wzgórz (C3.1). Pasterz nie jest zwykłym chłopem do scoringu pól 3/miasto. Nie mylić ze Wzgórzami i Owcami.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 900,
    keywords: ['pasterz', 'shepherd', 'owce', 'wilk'],
  },

  // ——— Major 10 ———
  {
    id: 'fig-acrobat',
    name: 'Akrobata (meeple na placu)',
    moduleId: 'major-10-circus-artists',
    wherePlaced:
      'Na placu akrobatycznym — do 3 akrobatów na placu (także różnych graczy); to Twoje zwykłe meeple ustawione jako akrobaci.',
    whenPlaced:
      'Etap 2: zamiast zwykłego umieszczenia na drodze/mieście możesz dołożyć meepla na plac akrobatyczny.',
    majorityStrength: 'Nie dotyczy dróg/miast — akrobaci nie budują większości feature mapy',
    scoring:
      'Piramida nie punktuje automatycznie przy trzecim akrobacie. Później, zamiast stawiania figury, możesz zapunktować pełną piramidę z Twoim akrobatą: 5 punktów za każdego akrobata. Na końcu gry każdy akrobata: 5 punktów.',
    whenReturns: 'Po punktowaniu piramidy (w trakcie lub na końcu) meeple wracają do zapasu.',
    restrictions:
      'To meeple gracza, ale w roli akrobaty nie scoruje drogi/miasta. Nie mylić z Cyrkiem Objazdowym C1/C2.',
    isPlayerMeeple: true,
    contributesToMajority: false,
    order: 1000,
    keywords: ['akrobata', 'piramida', 'cyrk'],
  },
  {
    id: 'fig-ringmistress',
    name: 'Dyrektorka cyrku',
    moduleId: 'major-10-circus-artists',
    wherePlaced: 'Na drodze, w mieście, w klasztorze lub na polu — jak specjalny meeple siły 1.',
    whenPlaced: 'Etap 2 tury zamiast zwykłego meepla na właśnie dołożonym kafelku (wolny obszar).',
    majorityStrength: '1',
    scoring:
      'Punktuje obszar jak meeple siły 1, a dodatkowo przy punktowaniu jej obszaru +2 za każdy plac cyrkowy lub akrobatyczny na jej kafelku i ośmiu sąsiadach.',
    whenReturns: 'Po punktowaniu obszaru wraca do zapasu jak zwykły meeple (chłop do końca, jeśli na polu).',
    restrictions:
      'Meeple gracza liczący się do większości (siła 1). Premia +2 dotyczy placów w zasięgu 8 sąsiadów jej kafelka.',
    isPlayerMeeple: true,
    contributesToMajority: true,
    order: 1010,
    keywords: ['dyrektorka', 'ringmistress', 'cyrk'],
  },

  // ——— Minis ———
  {
    id: 'fig-mini-messenger',
    name: 'Kurier (wiadomości / mini)',
    moduleId: 'mini-02-messengers',
    wherePlaced:
      'Na torze punktacji — na polu 0 obok znacznika punktów (nie na feature mapy).',
    whenPlaced: 'Przy przygotowaniu; potem przesuwasz go zamiast (lub zamiast) znacznika przy zdobywaniu punktów.',
    majorityStrength: 'Nie dotyczy — nie stoi na drogach/miastach i nie tworzy większości',
    scoring:
      'Gdy w Twojej turze znacznik lub kurier sam stanie na ciemnym polu (0, 5, 10…), dobierasz wiadomość. Na końcu: zsumuj znacznik + kurier przed punktowaniem końcowym.',
    whenReturns:
      'Przed punktowaniem końcowym zbierasz kuriera do zapasu po zsumowaniu wyniku.',
    restrictions:
      'Nie jest meeplem mapy. Nie mylić z Kurierem C3.1 (major-05). Współdzielone ciemne pole z figurą innego gracza = brak wiadomości.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 1120,
    keywords: ['kurier', 'wiadomość', 'wiadomosc', 'mini'],
  },
  {
    id: 'fig-ferry',
    name: 'Prom',
    moduleId: 'mini-03-ferries',
    wherePlaced:
      'Na jeziorze z wieloma zakończeniami dróg — łączy dokładnie dwa odcinki dróg.',
    whenPlaced:
      'Gdy jako podróżnik stawiasz meepla na właśnie dołożonym kafelku jeziora: weź prom z zapasu i ustaw połączenie. Potem możesz przestawiać prom przy przedłużaniu drogi.',
    majorityStrength: 'Nie dotyczy — żeton łączący drogi, nie meeple',
    scoring:
      'Sam nie daje punktów; wyznacza, które odcinki tworzą jedną drogę do scoringu (1 pkt/kafelek bez karczmy itd.).',
    whenReturns:
      'Pozostaje na jeziorze do końca gry (można przestawiać według zasad); nie wraca do zapasu jak meeple po scoringu.',
    restrictions:
      'Każdy prom raz na turę. Nie jest meeplem i nie liczy się do większości. Przestawienie promu ≠ podwójna tura budowniczego.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 1130,
    keywords: ['prom', 'ferry', 'jezioro'],
  },
  {
    id: 'fig-gold-bar',
    name: 'Sztabka złota',
    moduleId: 'mini-04-goldmines',
    wherePlaced:
      'Na kafelkach (nie na konkretnym obszarze): 1 na kafelku ze złotem + 1 na wybranym sąsiedzie (także ukośnym).',
    whenPlaced:
      'Po dołożeniu kafelka ze złotem — połóż dwie sztabki zanim dokończysz turę według sekwencji minidodatku.',
    majorityStrength: 'Nie dotyczy — żeton, nie meeple',
    scoring:
      'Przy ukończeniu obszaru większość zbiera sztabki z kafelków tego obszaru. Na końcu: 1–3 sztabki → 1 pkt/szt.; 4–6 → 2; 7–9 → 3; 10+ → 4.',
    whenReturns:
      'Zebrane sztabki zostają u gracza do scoringu końcowego złota. Nieodebrane zdejmij z planszy przed finałem.',
    restrictions:
      'Chłopi nigdy nie dostają złota. Nie jest meeplem i nie tworzy większości.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 1140,
    keywords: ['złoto', 'zloto', 'sztabka', 'gold'],
  },
  {
    id: 'fig-mage',
    name: 'Mag',
    moduleId: 'mini-05-mage-witch',
    wherePlaced:
      'Wyłącznie na nieukończonym mieście lub drodze (nie musi być właśnie dołożony kafelek). Nigdy razem z Wiedźmą na tym samym obszarze.',
    whenPlaced:
      'Gdy kładziesz kafelek z symbolem magii — przed meeplem wystaw lub przenieś Maga albo Wiedźmę.',
    majorityStrength: 'Nie dotyczy — figura neutralna, nie dodaje siły gracza',
    scoring:
      'Scorowany obszar z Magiem: dodatkowo +1 punkt za każdy kafelek obszaru (bez herbów). Potem zdejmij Maga na bok.',
    whenReturns:
      'Po zmodyfikowaniu wyniku na bok (wraca przy kolejnym symbolu magii). Brak legalnego miejsca = zdejmij jedną figurę magiczną na bok.',
    restrictions:
      'Nie jest meeplem gracza i nie tworzy większości. Nie może dzielić obszaru z Wiedźmą.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 1150,
    keywords: ['mag', 'mage'],
  },
  {
    id: 'fig-witch',
    name: 'Wiedźma',
    moduleId: 'mini-05-mage-witch',
    wherePlaced:
      'Wyłącznie na nieukończonym mieście lub drodze; nigdy na tym samym obszarze co Mag.',
    whenPlaced:
      'Przy symbolu magii — wystaw lub przenieś Maga albo Wiedźmę przed umieszczeniem meepla.',
    majorityStrength: 'Nie dotyczy — figura neutralna, nie dodaje siły gracza',
    scoring:
      'Punkty scorowanego obszaru dzielone na pół i zaokrąglane w górę (najpierw premia karczmy/katedry, potem połowa). Potem zdejmij Wiedźmę na bok.',
    whenReturns:
      'Po zmodyfikowaniu wyniku na bok; wraca przy kolejnym symbolu magii.',
    restrictions:
      'Nie jest meeplem gracza. Zaokrąglanie zawsze w górę. Nie może stać z Magiem na jednym obszarze.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 1151,
    keywords: ['wiedźma', 'wiedzma', 'witch'],
  },
  {
    id: 'fig-robber',
    name: 'Bandyta',
    moduleId: 'mini-06-robbers',
    wherePlaced:
      'Na torze punktacji — na polu zajętym przez co najmniej jeden znacznik punktów innego gracza.',
    whenPlaced:
      'Gdy dobierasz kafelek z workiem (i w kolejce inni bez bandyty na torze): ustaw lub przenieś bandytę na cudzy znacznik.',
    majorityStrength: 'Nie dotyczy — nie stoi na feature mapy',
    scoring:
      'Gdy śledzony znacznik zdobywa punkty: bierzesz połowę (w górę); ofiara zachowuje pełne. Musisz wziąć pierwszą okazję. Na końcu przed finałem: 3 punkty, jeśli bandyta nadal na torze.',
    whenReturns:
      'Po kradzieży do zapasu; przed punktowaniem końcowym po ewentualnych 3 pkt.',
    restrictions:
      'Nie jest meeplem mapy i nie tworzy większości. Nie okradasz siebie. Honor wśród złodziei: punkty z kradzieży bandyty nie triggerują innych bandytów na polu okradającego.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 1160,
    keywords: ['bandyta', 'robber', 'rabuś', 'rabus'],
  },

  // ——— Mgła ———
  {
    id: 'fig-mists-guard',
    name: 'Strażnik (Mgła — meeple kooperacji)',
    moduleId: 'mists-over-carcassonne',
    wherePlaced:
      'Na drodze lub w mieście (w kooperacji nigdy na polu). Od poz. 2 także na zamku (1 meeple, zamek wolny).',
    whenPlaced:
      'Etap 2b tury kooperacji — opcjonalnie po wystawieniu duchów. W classic-expansion obowiązują reguły klasycznego wariantu z duchami/zamkami/cmentarzami.',
    majorityStrength:
      'W kooperacji nie ma rywalizacyjnej większości: przy wielu kolorach na obiekcie doliczacie bazę ponownie za każdy dodatkowy kolor',
    scoring:
      'Ukończona droga/miasto: wybór A punkty na wspólny tor albo B usunięcie do 3 duchów z jednego kafelka. Po punktowaniu meeple wraca.',
    whenReturns:
      'Po scoringu obiektu do zapasu właściciela. Przy zamknięciu cmentarza pochowany meeple schodzi z gry według reguł poziomu.',
    restrictions:
      'W kooperacji nie stawiaj na polu. Brak duchów w zapasie przy wystawieniu = przegrana. To meeple gracza w trybie koop, ale scoring jest wspólny.',
    isPlayerMeeple: true,
    contributesToMajority: false,
    order: 2000,
    keywords: ['strażnik', 'straznik', 'mgła', 'mgla', 'meeple'],
  },
  {
    id: 'fig-ghost',
    name: 'Duch',
    moduleId: 'mists-over-carcassonne',
    wherePlaced:
      'Na kafelkach z mgłą (i na cmentarzach według poziomu) — zapas 15 duchów obok toru.',
    whenPlaced:
      'Etap 2a: jeśli kafelek ma mgłę i obszar nie został właśnie zamknięty, wystaw tyle duchów, ile symboli; przy rozszerzeniu obszaru mgły −1 (min. 0).',
    majorityStrength: 'Nie dotyczy — zagrożenie kooperacji / efekt classic, nie meeple gracza',
    scoring:
      'Sam nie daje punktów. Zamknięcie mgły zdejmuje duchy z obszaru. Alternatywa scoringu: usuń do 3 duchów z jednego kafelka zamiast punktów.',
    whenReturns:
      'Do zapasu przy zamknięciu mgły, usuwaniu za scoringu B, efektach psów/cmentarzy itd.',
    restrictions:
      'Nie jest meeplem gracza i nie tworzy większości. Brak duchów w zapasie, gdy musisz wystawić ≥1 = natychmiastowa przegrana kooperacji.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 2010,
    keywords: ['duch', 'ghost', 'duchy', 'mgła', 'mgla'],
  },
  {
    id: 'fig-hound',
    name: 'Pies (Rufus / Ronja)',
    moduleId: 'mists-over-carcassonne',
    wherePlaced:
      'Przypisany do meepla strażnika od poziomu 3 kooperacji (progi punktowe na torze zależą od poziomu).',
    whenPlaced:
      'Według setupu poziomu (np. Rufus/Ronja na polach progów). Aktywacja pomocy przy meeplu według karty poziomu.',
    majorityStrength: 'Nie dotyczy — nie jest meeplem do przewagi rywalizacyjnej',
    scoring:
      'Pomaga usuwać duchy (np. do 3 z sąsiednich kafelków). Po punktowaniu meepla z psem: 1 punkt za każdego ducha na planszy; tego scoringu psa nie wolno zamienić na usuwanie duchów.',
    whenReturns: 'Po punktowaniu meepla pies wraca do pudełka (usuń z gry).',
    restrictions:
      'Tylko tryb kooperacji poziomów z psami. Nie mylić z innymi figurami C3. Nie jest meeplem gracza do większości mapy.',
    isPlayerMeeple: false,
    contributesToMajority: false,
    order: 2020,
    keywords: ['pies', 'rufus', 'ronja', 'hound', 'dog'],
    requiredMistsPlayMode: 'coop',
  },
];

/** Modules that contribute at least one figure/pawn/token row. */
export function getFigureModuleIds(): CarcassonneModuleId[] {
  return Array.from(new Set(carcassonneFigureRules.map((r) => r.moduleId)));
}

export function getFigureRuleById(id: string): FigureRule | undefined {
  return carcassonneFigureRules.find((r) => r.id === id);
}

export function filterFigureRulesForModules(
  enabledModuleIds: CarcassonneModuleId[],
  mistsPlayMode: 'coop' | 'classic-expansion' = 'coop'
): FigureRule[] {
  const enabled = new Set(enabledModuleIds);
  return carcassonneFigureRules.filter((r) => {
    if (!enabled.has(r.moduleId)) return false;
    if (r.requiredMistsPlayMode && r.requiredMistsPlayMode !== mistsPlayMode) {
      return false;
    }
    return true;
  });
}

export function buildFiguresTable(
  enabledModuleIds: CarcassonneModuleId[],
  mistsPlayMode: 'coop' | 'classic-expansion' = 'coop'
): FigureTableRow[] {
  return filterFigureRulesForModules(enabledModuleIds, mistsPlayMode)
    .slice()
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .map((row) => {
      const mod = getModuleById(row.moduleId);
      return {
        id: row.id,
        name: row.name,
        moduleId: row.moduleId,
        moduleShortName: mod?.shortName ?? row.moduleId,
        wherePlaced: row.wherePlaced,
        whenPlaced: row.whenPlaced,
        majorityStrength: row.majorityStrength,
        scoring: row.scoring,
        whenReturns: row.whenReturns,
        restrictions: row.restrictions,
        isPlayerMeeple: row.isPlayerMeeple,
        contributesToMajority: row.contributesToMajority,
        keywords: [...(row.keywords ?? [])],
      };
    });
}

const REQUIRED_TEXT_FIELDS: Array<keyof FigureRule> = [
  'name',
  'wherePlaced',
  'whenPlaced',
  'majorityStrength',
  'scoring',
  'whenReturns',
  'restrictions',
];

const SHORTCUT_PATTERNS = [
  /działa normalnie/i,
  /jak zwykle(?!.*punkt)/i,
  /zajrzyj do/i,
  /według instrukcji/i,
  /\bplaceholder\b/i,
  /do uzupełnienia/i,
];

export type FigureValidationIssue = string;

export function validateFigureCatalogue(
  enabledModuleIds?: CarcassonneModuleId[]
): FigureValidationIssue[] {
  const issues: string[] = [];
  const seenIds = new Set<string>();

  for (const rule of carcassonneFigureRules) {
    if (seenIds.has(rule.id)) {
      issues.push(`Duplicate figure rule id: ${rule.id}`);
    }
    seenIds.add(rule.id);

    if (!getModuleById(rule.moduleId)) {
      issues.push(`Unknown module on figure rule ${rule.id}: ${rule.moduleId}`);
    }

    for (const field of REQUIRED_TEXT_FIELDS) {
      const value = rule[field];
      if (typeof value !== 'string' || !value.trim()) {
        issues.push(`Figure rule ${rule.id} missing instruction field: ${field}`);
      }
    }

    const blob = REQUIRED_TEXT_FIELDS.map((f) => String(rule[f])).join('\n');
    for (const re of SHORTCUT_PATTERNS) {
      if (re.test(blob)) {
        issues.push(
          `Figure rule ${rule.id} contains forbidden shortcut phrase matching ${re}`
        );
      }
    }
  }

  // Exact strengths required by product brief
  const large = getFigureRuleById('fig-large-meeple');
  if (!large || !large.majorityStrength.includes('2')) {
    issues.push('Large meeple must state exact majority strength 2');
  }
  const mayor = getFigureRuleById('fig-mayor');
  if (!mayor || !/\b0\b/.test(mayor.majorityStrength)) {
    issues.push('Mayor must state exact majority strength 0');
  }
  // Wagon must not appear — C3.1 uses messengers/mayors/scarecrows
  for (const rule of carcassonneFigureRules) {
    if (/wóz|woz|wagon/i.test(`${rule.name} ${rule.id}`)) {
      issues.push(`Unexpected wagon/wóz figure in C3.1 catalogue: ${rule.id}`);
    }
  }

  const readyFigureModules = getFigureModuleIds().filter(
    (id) => getModuleById(id)?.status === 'ready'
  );
  const allReady = Array.from(
    new Set<CarcassonneModuleId>(['base', ...readyFigureModules])
  );

  const toCheck: CarcassonneModuleId[][] = enabledModuleIds
    ? [enabledModuleIds]
    : [['base'], ['base', 'river', 'abbot'], allReady];

  for (const profile of toCheck) {
    const table = buildFiguresTable(profile);
    if (profile.includes('base') && table.length === 0) {
      issues.push(`Empty figures table for profile ${profile.join('+')}`);
    }
    for (const mid of profile) {
      if (!getFigureModuleIds().includes(mid)) continue;
      if (getModuleById(mid)?.status !== 'ready') continue;
      if (!table.some((r) => r.moduleId === mid)) {
        issues.push(
          `Active figure module ${mid} has no table row in profile ${profile.join('+')}`
        );
      }
    }
  }

  return issues;
}
